import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { useWindowSize } from '../utils/useWindowSize';
import { getCentreReferralLink, getSoloReferralLink, getMyReferrals } from '../utils/api';
import { FiShare2, FiCopy, FiDownload, FiRefreshCw, FiCheckCircle } from 'react-icons/fi';

const STATUS_COLORS = {
  clicked:    { background: '#eaf0fb', color: '#2980b9' },
  registered: { background: '#eafaf1', color: '#27ae60' },
  approved:   { background: '#fef9e7', color: '#d4ac0d' },
  rewarded:   { background: '#f9ebff', color: '#8e44ad' },
};

function badgeStyle(status) {
  return STATUS_COLORS[status] || { background: '#f0f0f0', color: '#666' };
}

const Referrals = () => {
  const { user }      = useAuth();
  const { isMobile }  = useWindowSize();

  const isSoloHealer = user?.role === 'healer';

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [copied,  setCopied]  = useState(false);
  const [stats,   setStats]   = useState(null);
  const [history, setHistory] = useState([]);

  const [activeType, setActiveType] = useState(isSoloHealer ? 'solo_healer' : 'patient');
  const pendingTypeRef = useRef(null);

  const TABS = isSoloHealer
    ? [
        { key: 'solo_healer', label: 'Refer a Solo Healer', reward: true },
        { key: 'centre',      label: 'Refer a Centre',      reward: true },
      ]
    : [
        { key: 'patient',     label: 'Invite a Patient',       reward: false },
        { key: 'healer',      label: 'Invite a Centre Healer', reward: false },
        { key: 'solo_healer', label: 'Refer a Solo Healer',    reward: true  },
        { key: 'centre',      label: 'Refer a Centre',         reward: true  },
      ];
  const activeTab = TABS.find(t => t.key === activeType) || TABS[0];

  const fetchLink = useCallback(async () => {
    const thisType = activeType;
    pendingTypeRef.current = thisType;
    setLoading(true);
    setError('');
    try {
      const [linkSettled, statsSettled] = await Promise.allSettled([
        isSoloHealer ? getSoloReferralLink(activeType) : getCentreReferralLink(activeType),
        getMyReferrals(),
      ]);
      if (pendingTypeRef.current !== thisType) return;
      if (linkSettled.status === 'rejected') throw linkSettled.reason;
      const linkRes  = linkSettled.value;
      const statsRes = statsSettled.status === 'fulfilled' ? statsSettled.value : null;
      if (statsSettled.status === 'rejected') {
        console.warn('[Referrals] stats fetch failed', statsSettled.reason?.response?.status);
      }
      setData(linkRes.data.data);
      if (statsRes?.data?.data) {
        setStats(statsRes.data.data.stats   || null);
        setHistory(statsRes.data.data.history || []);
      }
    } catch (err) {
      if (pendingTypeRef.current !== thisType) return;
      setError(err.response?.data?.message || 'Could not load referral link.');
    } finally {
      if (pendingTypeRef.current === thisType) setLoading(false);
    }
  }, [isSoloHealer, activeType]);

  useEffect(() => { fetchLink(); }, [fetchLink]);

  const handleCopy = async () => {
    if (!data?.url) return;
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // clipboard permission denied — silently ignore
    }
  };

  const handleDownloadQR = () => {
    if (!data?.qrCodeBase64) return;
    const a      = document.createElement('a');
    a.href       = data.qrCodeBase64;
    a.download   = 'lifechiai-referral-qr.png';
    a.click();
  };

  return (
    <div style={S.layout}>
      <Sidebar />
      <div style={{ ...S.main, marginLeft: isMobile ? 0 : '260px', padding: isMobile ? '56px 16px 16px' : '32px' }}>

        <div style={S.header}>
          <div>
            <h1 style={S.title}>Referrals</h1>
            <p style={S.sub}>Share your referral link to invite new members</p>
          </div>
          <button style={S.refreshBtn} onClick={fetchLink} disabled={loading} type="button">
            <FiRefreshCw size={13} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error && <div style={S.errorBox}>{error}</div>}

        {/* Type selector tabs */}
        <div style={S.tabs}>
          {TABS.map(t => (
            <button
              key={t.key}
              style={activeType === t.key ? S.tabActive : S.tab}
              onClick={() => setActiveType(t.key)}
              type="button"
            >
              {t.label}
              {t.reward && (
                <span style={{
                  ...S.rewardBadge,
                  background: activeType === t.key ? 'rgba(255,255,255,0.25)' : '#dcfce7',
                  color:      activeType === t.key ? 'inherit' : '#166534',
                }}>
                  Reward eligible
                </span>
              )}
            </button>
          ))}
        </div>

        {loading && !data ? (
          <div style={S.loading}>Loading referral link…</div>
        ) : data ? (
          <div style={S.card}>
            <div style={S.cardHeader}>
              <FiShare2 size={16} style={{ marginRight: 8 }} />
              {activeTab.label}
            </div>

            {activeTab.reward && (
              <div style={S.rewardNote}>
                You earn a bonus reward when this referral is approved.
              </div>
            )}

            {/* Link + copy */}
            <div style={S.linkRow}>
              <input
                readOnly
                value={data.url || ''}
                style={S.linkInput}
                onClick={e => e.target.select()}
              />
              <button style={copied ? S.copiedBtn : S.copyBtn} onClick={handleCopy} type="button">
                {copied
                  ? <><FiCheckCircle size={13} style={{ marginRight: 5 }} />Copied</>
                  : <><FiCopy        size={13} style={{ marginRight: 5 }} />Copy Link</>}
              </button>
            </div>

            {/* QR code */}
            {data.qrCodeBase64 && (
              <div style={S.qrSection}>
                <div style={S.qrLabel}>QR Code</div>
                <img
                  src={data.qrCodeBase64}
                  alt="Referral QR code"
                  style={S.qrImage}
                />
                <button style={S.downloadBtn} onClick={handleDownloadQR} type="button">
                  <FiDownload size={13} style={{ marginRight: 6 }} />
                  Download QR
                </button>
              </div>
            )}

            <div style={S.hint}>
              Anyone who opens this link will land on the correct registration page with your referral pre-filled.
            </div>

            {/* Stats */}
            {stats && (
              <div style={S.statsSection}>
                {[
                  { label: 'Sent',       value: stats.clicked    ?? 0 },
                  { label: 'Registered', value: stats.registered ?? 0 },
                  { label: 'Approved',   value: stats.approved   ?? 0 },
                  { label: 'Rewarded',   value: stats.rewarded   ?? 0 },
                ].map(({ label, value }) => (
                  <div key={label} style={S.statCard}>
                    <div style={S.statValue}>{value}</div>
                    <div style={S.statLabel}>{label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* History table */}
            {history.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <div style={S.tableLabel}>History</div>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} style={i % 2 === 0 ? S.rowEven : S.rowOdd}>
                        <td style={S.td}>{h.referralType}</td>
                        <td style={S.td}>
                          <span style={{ ...S.badge, ...badgeStyle(h.status) }}>{h.status}</span>
                        </td>
                        <td style={S.td}>
                          {h.date ? new Date(h.date).toLocaleDateString('en-IN') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

      </div>
    </div>
  );
};

const S = {
  layout:      { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  main:        { flex: 1, padding: '32px' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  title:       { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
  sub:         { fontSize: '13px', color: '#666', margin: 0 },
  refreshBtn:  { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: '#4a8f82', fontFamily: "'Raleway', sans-serif", minHeight: '38px' },
  errorBox:    { background: '#fdedec', color: '#e74c3c', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', marginBottom: '20px', border: '1px solid #fadbd8' },
  loading:     { textAlign: 'center', padding: '48px', color: '#888', fontSize: '14px' },
  // Type selector tabs
  tabs:        { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  tab:         { padding: '8px 14px', borderRadius: '20px', border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#555', fontFamily: "'Raleway', sans-serif", display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  tabActive:   { padding: '8px 14px', borderRadius: '20px', border: 'none', background: '#4a8f82', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: '#fff', fontFamily: "'Raleway', sans-serif", display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  rewardBadge: { fontSize: '10px', fontWeight: '700', background: 'rgba(255,255,255,0.25)', padding: '2px 5px', borderRadius: '8px' },
  rewardNote:  { background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' },
  // Link card
  card:        { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '28px', maxWidth: '560px' },
  cardHeader:  { display: 'flex', alignItems: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '20px' },
  linkRow:     { display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' },
  linkInput:   { flex: 1, minWidth: '200px', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', fontFamily: "'Open Sans', sans-serif", color: '#333', background: '#f9faf7', outline: 'none', cursor: 'text' },
  copyBtn:     { display: 'flex', alignItems: 'center', padding: '10px 18px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  copiedBtn:   { display: 'flex', alignItems: 'center', padding: '10px 18px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'default', whiteSpace: 'nowrap' },
  qrSection:   { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginBottom: '20px', padding: '20px', background: '#f9faf7', borderRadius: '10px', border: '1px solid #e8f0e8' },
  qrLabel:     { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', fontFamily: "'Raleway', sans-serif" },
  qrImage:     { width: '180px', height: '180px', borderRadius: '8px' },
  downloadBtn: { display: 'flex', alignItems: 'center', padding: '8px 18px', background: 'transparent', color: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  hint:        { fontSize: '12px', color: '#888', lineHeight: '1.6', marginBottom: '24px' },
  // Stats
  statsSection: { display: 'flex', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' },
  statCard:     { flex: '1 1 100px', background: '#f9faf7', border: '1px solid #e8f0e8', borderRadius: '8px', padding: '14px 10px', textAlign: 'center' },
  statValue:    { fontFamily: "'Raleway', sans-serif", fontSize: '22px', fontWeight: '700', color: '#4a8f82', marginBottom: '4px' },
  statLabel:    { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#888' },
  // History
  tableLabel:  { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '10px', fontFamily: "'Raleway', sans-serif" },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:          { textAlign: 'left', padding: '8px 10px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e8f0e8' },
  td:          { padding: '9px 10px', color: '#333', verticalAlign: 'middle' },
  rowEven:     { background: '#fff' },
  rowOdd:      { background: '#f9faf7' },
  badge:       { display: 'inline-block', padding: '2px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif" },
};

export default Referrals;
